/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, memo, useState, useCallback, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { usePromiseTracker } from 'react-promise-tracker';

import { getCardsData } from 'store/customer/customer-view-actions';
import Tile from 'components/shared/tiles/Tile';
import classes from './TileContainer.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { NUMBER_ZERO, areas } from 'constants/constants';
import { Sync } from 'components/shared/loader/Sync';
import TilesLoadedMessage from 'components/shared/message/TilesLoadedMessage';
import InfiniteScroll from 'react-infinite-scroll-component';
import { calculateItemsPerPage } from 'utils/utils';
import { setCards } from 'store/customer/customerViewSlice';

const TileContainer = memo(({ page }) => {
  const dispatch = useDispatch();
  const cards = useSelector((state) => state?.CustomerView?.cards);
  const refreshTiles = useSelector((state) => state?.HomeMyView?.refreshTiles);
  const totalTileCount = useSelector(
    (state) => state?.HomeMyView?.totalTileCount
  );
  const selectedInsightView = useSelector(
    (state) => state?.CustomerView?.selectedInsightView
  );
  const insightType = useSelector((state) => state?.CustomerView?.insightType);

  const allInsightViews = useSelector(
    (state) => state?.CustomerView?.allInsightViews
  );
  const queryClient = useQueryClient();
  const { promiseInProgress } = usePromiseTracker({ area: areas.customerView });

  // Pagination state
  const [offset, setOffset] = useState(NUMBER_ZERO);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const itemsPerPage = useMemo(
    () => calculateItemsPerPage(),
    [window.innerHeight, window.innerWidth]
  );

  useEffect(() => {
    if (allInsightViews?.length > 0) {
      setOffset(NUMBER_ZERO);
      setInitialLoadComplete(false);

      // Fetch cards when selectedInsightView changes
      fetchCards(NUMBER_ZERO);
    }
  }, [selectedInsightView?.Id, allInsightViews?.length, refreshTiles]);

  // Load All tile and Refresh the page before getting cards data api, analytics api is getting called for all 12 cards

  useEffect(() => {
    const handleUnload = () => {
      dispatch(setCards([]));
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const fetchCards = async (offset) => {
    dispatch(
      getCardsData(
        insightType,
        selectedInsightView?.Id,
        queryClient,
        offset,
        itemsPerPage
      )
    ).then(() => {
      setOffset(offset);
      setInitialLoadComplete(true);
      if (offset > 0) {
        setIsLoadingMore(false);
      }
    });
  };

  const fetchMoreData = useCallback(async () => {
    // Prevent fetching if initial load hasn't completed yet or already loading
    if (!initialLoadComplete || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    const newOffset = offset + itemsPerPage;

    await fetchCards(newOffset);

    // Reset loading state after a short delay to prevent rapid duplicate calls
  }, [initialLoadComplete, isLoadingMore, offset, itemsPerPage]);

  // Check if there are more items to load
  const hasMore = cards.length < totalTileCount;

  return (
    <div className='tile-container'>
      {cards?.length > 0 && (
        <div
          md={12}
          className={
            classes[
              ('tile-container-drag-drop-container',
              'infinite-scroll-container')
            ]
          }>
          <Row className='list-manager-insights g-3'>
            <InfiniteScroll
              dataLength={cards.length}
              next={fetchMoreData}
              hasMore={hasMore && initialLoadComplete}
              loader={null}
              endMessage={<TilesLoadedMessage />}
              scrollThreshold={0.7} // Trigger when 70% scrolled
            >
              <Row className='list-manager g-3'>
                {cards?.map((item, index) => (
                  <Col
                    key={item.cardId}
                    sm={12}
                    md={6}
                    xxl={4}>
                    <Tile
                      card={item}
                      page={page}
                      area={item.cardId}
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          </Row>
        </div>
      )}

      {promiseInProgress && (
        <div className='d-flex justify-content-center'>
          <p className={`title title__normal ${classes['loading-text']}`}>
            Loading
          </p>
          <Sync area={areas.customerView} />
        </div>
      )}
    </div>
  );
});

export default TileContainer;
